<template>
  <ToolShell
    tool-name="Late fee calculator"
    h1="Late fee calculator for overdue invoices."
    lede="A late fee is usually the invoice amount times your annual rate, pro-rated for the days late. This calculator works it out for one overdue invoice from the amount, the rate in your terms, and the days late. Free, instant, no signup."
    :trust="['No signup', 'Instant results', 'Pro-rated daily rate']"
    form-title="Your numbers"
    :upsell-points="[
      'Automated reminder sequences follow up on a schedule',
      'Reminders branch on whether the invoice is still unpaid',
      'The fee stays the backup, not the strategy',
    ]"
    free-label="The free calculator"
    :free-bullets="[
      'The late fee for one overdue invoice, calculated once',
      'You work out and apply the fee yourself',
      'Preventing the overdue invoice is on you too',
    ]"
    upgrade-headline="This number works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Reminder emails send on a schedule on every plan, and texts from Voyager',
      'Follow-ups branch on paid status automatically',
      'Payment links collect online',
      'Everything lands on the client record — status always visible',
    ]"
    slug="/tools/late-fee-calculator"
    :meta-description="metaDescription"
    doc-brand="Late fee calculator"
    :doc-subtitle="invoiceSummary"
    doc-kind="SUMMARY"
    stamp="Result"
  >
    <template #form>
      <label class="ta-field">
        <span>Invoice amount ($)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.amount" type="number" min="0" step="0.01" placeholder="2400" />
        </div>
      </label>

      <label class="ta-field">
        <span>Annual late fee rate (%)</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5l-9 9" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="4.8" cy="4.8" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="11.2" cy="11.2" r="1.8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>
          <input v-model.number="form.rate" type="number" min="0" step="0.5" placeholder="12" />
        </div>
      </label>

      <label class="ta-field">
        <span>Days overdue</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.number="form.days" type="number" min="0" step="1" placeholder="21" />
        </div>
      </label>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Invoice amount</span><strong>{{ money(form.amount) }}</strong></div>
        <div><span>Annual rate</span><strong>{{ form.rate || 0 }}%</strong></div>
        <div><span>Days overdue</span><strong>{{ form.days || 0 }}</strong></div>
        <div><span>New balance due</span><strong>{{ money(total) }}</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Breakdown</th><th>Value</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Daily rate ({{ form.rate || 0 }}% / 365)</td>
              <td class="ta-num">{{ dailyPct }}%</td>
            </tr>
            <tr>
              <td>Fee per day</td>
              <td class="ta-num">{{ money(feePerDay) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total" :class="{ 'ta-doc-total--long': money(fee).length > 11 }">
        <span>Late fee</span>
        <strong>{{ money(fee) }}</strong>
      </div>

      <p class="ta-doc-note">Always state late fees in your payment terms before invoicing — a fee the client never agreed to is hard to collect.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Enter the invoice amount</h2><p>The overdue balance the fee applies to.</p></div></div>
      <div><span>02</span><div><h2>Add your terms</h2><p>The annual rate from your payment terms and how many days the invoice is late.</p></div></div>
      <div><span>03</span><div><h2>Read the fee</h2><p>The pro-rated fee, the per-day breakdown, and the new balance due update live.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Late fee FAQ" :faq="faq" />

    <RelatedPages path="/tools/late-fee-calculator" title="Late fee calculator" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";

const form = reactive({ amount: 2400, rate: 12, days: 21 });

const fee = computed(() =>
  (Number(form.amount) || 0) * ((Number(form.rate) || 0) / 100 / 365) * (Number(form.days) || 0),
);
const total = computed(() => (Number(form.amount) || 0) + fee.value);
const feePerDay = computed(() => (Number(form.amount) || 0) * ((Number(form.rate) || 0) / 100 / 365));
const dailyPct = computed(() => ((Number(form.rate) || 0) / 365).toFixed(4));

const money = (v) =>
  `$${(Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const invoiceSummary = computed(
  () => `${money(form.amount)} invoice · ${form.days || 0} days overdue`,
);

const guideSections = [
  {
    title: "How the calculator works it out",
    paragraphs: [
      "It turns your annual rate into a daily rate by dividing it by 365, multiplies that by the invoice amount to get the fee per day, then multiplies by the days overdue. The new balance due is the invoice amount plus the fee.",
      "Late fee = invoice amount × (annual rate ÷ 365) × days overdue. The fee is simple, not compounded: each day adds the same amount.",
    ],
  },
  {
    title: "Worked examples",
    bullets: [
      "$2,400 at 12% a year, 21 days late: about $0.79 a day, a $16.57 fee and $2,416.57 due.",
      "$5,000 at 18% a year, 30 days late: a $73.97 fee and $5,073.97 due.",
      "$800 at 10% a year, 45 days late: a $9.86 fee and $809.86 due.",
    ],
    paragraphs: [
      "On most invoices a pro-rated fee is small. Its job is to give the client a reason to pay this invoice before the others, not to make money.",
    ],
  },
  {
    title: "Monthly rates and flat fees",
    paragraphs: [
      "If your terms state a monthly rate, multiply it by 12 and enter that: 1.5% a month is 18% a year. If your terms use a flat fee, such as $25 once the invoice is late, you do not need the calculator; add that amount to the balance.",
    ],
  },
  {
    title: "Agree the fee before you invoice",
    paragraphs: [
      "A late fee is easiest to collect when the client agreed to it before the work started. Put the rate, when it starts and how it is worked out in the proposal or contract, and repeat it on every invoice. The limits on late fees and interest differ by place and by type of client, so check the rules where you and your client are before you set a rate.",
    ],
    links: [
      { label: "Net 30 payment terms", path: "/learn/net-30-payment-terms" },
      { label: "Invoice due date calculator", path: "/tools/invoice-due-date-calculator" },
    ],
  },
  {
    title: "Charging it",
    paragraphs: [
      "Tell the client before you add the fee, with the number and how you worked it out. Add it as its own line, so the original amount stays clear. Some businesses waive the first late fee for a good client who pays straight away; decide that before you send, not after the client asks.",
    ],
    links: [
      { label: "Payment reminder generator", path: "/tools/payment-reminder-generator" },
    ],
  },
  {
    title: "Late fees in Workspace369",
    paragraphs: [
      "Automatic late fees start on Commander. You choose a fee type, such as a flat amount or a percentage of the overdue balance, a grace period of days after the due date before a fee applies, and caps on the total. Fees can apply automatically or wait for your approval, and each fee you apply or waive is listed in the late fee history. Late-payment reminders are on every plan.",
    ],
    links: [
      { label: "Invoices", path: "/features/invoices" },
      { label: "Workflow automations", path: "/features/automation" },
    ],
  },
];

const faq = [
  {
    q: "How is a late fee calculated on an invoice?",
    a: "The common approach is a pro-rated annual rate: invoice amount × (annual rate ÷ 365) × days overdue. A 12% annual rate on a $2,400 invoice works out to about $0.79 per day.",
  },
  {
    q: "Can I charge whatever late fee I want?",
    a: "No. The fee should be in the payment terms the client agreed to before the invoice, and within the limits where you work. Put it in the proposal and on the invoice itself.",
  },
  {
    q: "Are late fees better than reminders?",
    a: "Reminders can head off many overdue invoices before fees ever apply. Automated reminder sequences in Workspace369 follow up on a schedule and branch on paid status. The fee is the backup, not the strategy.",
  },
  {
    q: "Is 1.5% a month the same as 18% a year?",
    a: "Yes, as a simple rate: 1.5% × 12 months = 18%. Enter 18 in the calculator to apply a 1.5% monthly rate pro-rated by the day.",
  },
  {
    q: "What is a grace period on a late fee?",
    a: "The number of days after the due date before a fee applies. A short grace period, such as a few days, covers payments that are already on their way.",
  },
];

const metaTitle = "Late Fee Calculator | Workspace369 Free Tools";
const metaDescription =
  "Calculate the late fee on an overdue invoice from the annual fee rate and days overdue. Free instant calculator — plus the new balance due.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
}));
</script>
